import { useEffect, useState } from 'react';

import { productionApi } from 'api/production';
import { getWeekNumber, getWeekYear } from 'utils/dateUtils/dateUtils';

/**
 * Hook to fetch pack groups from the server for a given date
 *
 * @param {Date} date - The date to fetch pack groups for
 * @returns {Array} .packGroups - The pack groups for the given date
 */
export function usePackGroups(date) {
    const [packGroups, setPackGroups] = useState([]);

    useEffect(() => {
        if (!date) return;

        const fetchPackGroups = async () => {
            try {
                const response = await productionApi.getPackGroups({
                    week: getWeekNumber(date, 'YYYY-MM-DD'),
                    year: getWeekYear(date, 'YYYY-MM-DD'),
                });
                // Filter out non-standard pack groups (ex. "LS CHI1", "ST CHI1", "SM CHI1")
                const filteredPackGroups = response.data.packGroups.filter(
                    (packGroup) => packGroup.name.split(' ').length === 1,
                );
                setPackGroups(filteredPackGroups);
            } catch (error) {
                console.error('Error fetching pack groups:', error);
            }
        };

        fetchPackGroups();
    }, [date]);

    return { packGroups };
}

/**
 * Hook to fetch storefront configurations from the server for a given pack group ID
 *
 * @param {string} packGroupID - The ID of the pack group to fetch configurations for
 * @returns {Array} .configurations - The storefront configurations for the given pack group ID
 */
export function useStorefrontConfigs(packGroupID) {
    const [configs, setConfigs] = useState([]);

    useEffect(() => {
        if (!packGroupID) return;

        const fetchConfigs = async () => {
            try {
                const response = await productionApi.getStorefrontConfigurationsByPackGroupID(packGroupID);
                setConfigs(response.data.configurations);
            } catch (error) {
                console.error('Error fetching configurations:', error);
            }
        };

        fetchConfigs();
    }, [packGroupID]);

    return { configs };
}
